import { io, Socket } from 'socket.io-client';

export type FileProgressData = {
  file: {
    _id: string;
    fileName: string;
    fileSize: number;
    readySize: number;
    isComplete: boolean;
    filePath?: string;
    isInProgress?: boolean;
    timeLeftSeconds?: number;
  };
};

export type BackgroundProgressData = {
  id: string;
  loaded: number;
  total: number;
  bitrate: number | null;
};

export type BackgroundDoneData = {
  id: string;
  fileName: string;
  fileSize: number;
};

export type BackgroundErrorData = {
  id: string;
  error: string;
};

type SocketEvents = {
  'file-progress': (data: FileProgressData) => void;
  'file-deleted': () => void;
  'background-progress': (data: BackgroundProgressData) => void;
  'background-done': (data: BackgroundDoneData) => void;
  'background-error': (data: BackgroundErrorData) => void;
};

class SocketService {
  private _socket: Socket | null = null;
  private _listeners: { [K in keyof SocketEvents]?: SocketEvents[K][] } = {};

  connect() {
    if (this._socket?.connected) return;

    this._socket = io({
      transports: ['polling', 'websocket'],
      withCredentials: true,
    });

    this._socket.on('connect', () => {
      console.log('Socket.io connected');
    });

    this._socket.on('disconnect', () => {
      console.log('Socket.io disconnected');
    });

    // Re-wire all registered event listeners
    for (const event of Object.keys(this._listeners) as (keyof SocketEvents)[]) {
      for (const cb of this._listeners[event] || []) {
        this._socket.on(event as string, cb as (...args: unknown[]) => void);
      }
    }
  }

  on<K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]) {
    if (!this._listeners[event]) {
      this._listeners[event] = [];
    }
    this._listeners[event]!.push(callback);
    this._socket?.on(event as string, callback as (...args: unknown[]) => void);
  }

  off<K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]) {
    const list = this._listeners[event];
    if (list) {
      this._listeners[event] = list.filter(cb => cb !== callback) as typeof list;
    }
    this._socket?.off(event as string, callback as (...args: unknown[]) => void);
  }

  watchFile(fileId: string) {
    this._socket?.emit('watch-file', fileId);
  }

  disconnect() {
    this._socket?.disconnect();
    this._socket = null;
  }
}

export const socketService = new SocketService();
